import React from 'react';
import type { IEnvelopeState } from '../../store/reducers/envelope';
import type { IBlock, IGroup, ISignatureSettings } from '../../store/types';
import { Block } from '../Block';
// eslint-disable-next-line import/no-named-as-default -- withStyles
import GroupBox from '../GroupBox';

export type PdfBlocksProps = {
  blocks: { [blockId: string]: IBlock | undefined };
  groups?: { [groupId: string]: IGroup };
  envelope: IEnvelopeState;
  signingExperienceBlocks: IBlock[];
  currentSelectedBlockIndex: number | null;
  signingExperienceComplete: boolean;
  zoom: number;
  pageScale: number;
  setBlocksInGroupToComplete: (blocks: IBlock[]) => void;
  fullPage: number;
  scrollToBlock: (blockElement: HTMLDivElement, fullPage: number) => void;
  pageDimensions: { width?: number; height?: number };
  clickSignature: (blockId: string, tabIndex?: number) => void;
  updateBlock: (blockId: string, blockUpdate: any) => void;
  tabToBlock: (nextIndex: number) => void;
  signatureSettings?: ISignatureSettings;
  selectedBlock: IBlock | undefined;
};

export const PdfBlocks = React.memo(function PdfBlocks(props: PdfBlocksProps) {
  const {
    blocks,
    groups,
    envelope,
    signingExperienceBlocks,
    currentSelectedBlockIndex,
    signingExperienceComplete,
    zoom,
    pageScale,
    setBlocksInGroupToComplete,
    fullPage,
    scrollToBlock,
    pageDimensions,
    clickSignature,
    updateBlock,
    tabToBlock,
    signatureSettings,
  } = props;

  // This is weird 👇 - Zach
  const [keyIndex, setKeyIndex] = React.useState(0);
  React.useEffect(() => setKeyIndex((index) => index + 1), [blocks]);

  //  This was clearly intended to be used at some point but setPageAnnotations was no longer being called so it would always be undefine???
  // const [pageAnnotations, setPageAnnotations] = React.useState<IAnnotation[] | undefined>();

  function scrollToBlockOnThisPage(blockElement: HTMLDivElement) {
    scrollToBlock(blockElement, fullPage);
  }

  function getBlockIndexFromSigningExperience(block: IBlock) {
    return signingExperienceBlocks.findIndex((b) => b.blockId === block.blockId);
  }

  // Map object to keep track of how many of each type of block we have
  const blockCounts: Record<string, number> = {};

  const groupElements =
    groups &&
    envelope.envelope!.status !== 'Completed' &&
    Object.entries(groups).map(([groupId, group]) => {
      const groupBlocks = group.blockIds.map((blockId: string) => blocks[blockId]).filter(Boolean) as IBlock[];
      return (
        <GroupBox
          key={groupId}
          blocks={groupBlocks}
          zoom={zoom}
          clickSignature={clickSignature}
          updateBlock={updateBlock}
          group={group}
          tabIndex={getBlockIndexFromSigningExperience(groupBlocks[0])}
          currentSelectedBlockIndex={currentSelectedBlockIndex}
          signingExperienceBlocks={signingExperienceBlocks}
          signingExperienceComplete={signingExperienceComplete}
          envelopeStatus={envelope.envelope!.status}
          tabToBlock={tabToBlock}
          pageDimensions={pageDimensions}
          scrollToBlock={scrollToBlockOnThisPage}
          setBlocksInGroupToComplete={setBlocksInGroupToComplete}
        />
      );
    });

  const blockElements = Object.entries(blocks)
    .filter(([_blockId, block]) => block && !block.groupId)
    .map(([blockId, block]) => {
      if (!block) return null;

      const blockType = block.blockType;
      if (!blockCounts[blockType]) blockCounts[blockType] = 1;
      else blockCounts[blockType] += 1;

      return (
        <Block
          key={blockId}
          keyIndex={keyIndex}
          block={block}
          index={blockCounts[blockType]}
          zoom={zoom}
          clickSignature={clickSignature.bind(null, blockId)}
          updateBlock={updateBlock.bind(null, blockId)}
          signer={envelope.envelope!.signer}
          tabIndex={getBlockIndexFromSigningExperience(block)}
          currentSelectedBlockIndex={currentSelectedBlockIndex}
          signingExperienceComplete={signingExperienceComplete}
          tabToBlock={tabToBlock}
          pageDimensions={pageDimensions}
          pageScale={pageScale}
          signatureSettings={signatureSettings}
          scrollToBlock={scrollToBlockOnThisPage}
          dateFormatPreference={envelope.envelope!.config.dateFormat}
        />
      );
    });

  return (
    <>
      {groupElements || null}
      {blockElements || null}
    </>
  );
});
